<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'

interface IProps {
  dialogWidth: number
  dialogHeight: number
  minWidth?: number
  minHeight?: number
}

const props = withDefaults(defineProps<IProps>(), {
  dialogWidth: 920,
  dialogHeight: 600,
  minWidth: 300,
  minHeight: 200,
})

interface Emit {
  (e: 'update:dialogWidth', value: number): void
  (e: 'update:dialogHeight', value: number): void
  (e: 'onStartResizing'): void
}

const emit = defineEmits<Emit>()

const isResizing = ref(false)

// Initial mouse positions
let startX = 0
let startY = 0
let startWidth = props.dialogWidth
let startHeight = props.dialogHeight

// Default minimum sizes (if not passed as props)
const minWidth = props.minWidth || 300
const minHeight = props.minHeight || 200

// Mouse down triggers resizing
const startResizing = (e: MouseEvent) => {
  emit('onStartResizing')

  isResizing.value = true
  startX = e.clientX
  startY = e.clientY
  startWidth = props.dialogWidth
  startHeight = props.dialogHeight
  document.addEventListener('mousemove', resize)
  document.addEventListener('mouseup', stopResizing)
}

// Resize logic
const resize = (e: MouseEvent) => {
  if (!isResizing.value) return
  const dx = e.clientX - startX
  const dy = e.clientY - startY
  const newWidth = Math.max(minWidth, startWidth + dx)
  const newHeight = Math.max(minHeight, startHeight + dy)
  emit('update:dialogWidth', newWidth)
  emit('update:dialogHeight', newHeight)
}

// Cleanup listeners on unmount
onBeforeUnmount(() => {
  stopResizing()
})

// Stop resizing
const stopResizing = () => {
  isResizing.value = false
  document.removeEventListener('mousemove', resize)
  document.removeEventListener('mouseup', stopResizing)
}
</script>

<template>
  <VBtn
    class="resize-handle rounded-sm"
    size=""
    color="light"
    @mousedown="startResizing"
  >
    <VIcon
      icon="tabler-arrows-maximize"
    />
  </VBtn>
</template>

<style lang="scss" scoped>
.resize-handle {
  position: absolute;
  z-index: 1;
  bottom: -10px;
  right: -10px;
  cursor: nwse-resize;
  padding: 1px;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity)) !important;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
